import { ErrorInfo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "antd";
import { useForm } from "antd/es/form/Form";
import { track } from "@amplitude/analytics-browser";
import { useHotelDynamicDetails } from "src/Modules/Hotels/Pages/HotelDetails/context";
import useStepFlow from "src/Modules/Hotels/hooks/useStepsFlow";
import { GuestsInputType } from "src/Modules/Hotels/constants";
import StepButtons from "../../../StepButtons/StepButtons";
import GuestsFormList from "../GuestsFormList/GuestsFormList";
import { buildGuestsList, generateInitialGuestValues } from "../../utils";

export default function GuestForm() {
  const { t } = useTranslation();
  const { searchObject, guestsState, primaryGuestKeyState, policiesAcceptedState, rateConditions, isRateConditionsFetching } =
    useHotelDynamicDetails();
  const { validateStep, invalidateStep, goToNextStep } = useStepFlow();
  const [form] = useForm();
  const [guestValues, setGuestValues] = guestsState;
  const [primaryGuestKey, setPrimaryGuestKey] = primaryGuestKeyState;
  const guests = useMemo(() => buildGuestsList(searchObject, t), [searchObject, t]);
  const activeKeyState = useState(guests[0]?.key ?? null);
  const [activeKey, setActiveKey] = activeKeyState;
  const [policiesAccepted, setPoliciesAccepted] = policiesAcceptedState;
  const watchedPoliciesAccepted = Form.useWatch("policiesAccepted", form);
  const hasRateConditions = Boolean(!isRateConditionsFetching && rateConditions?.length);
  const formContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (watchedPoliciesAccepted !== undefined) setPoliciesAccepted(watchedPoliciesAccepted);
  }, [watchedPoliciesAccepted]);

  const scrollIntoView = () => {
    formContainerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleFinish = (values: GuestsInputType & { policiesAccepted?: boolean }) => {
    const { policiesAccepted: _policiesAccepted, ...guestValues } = values;
    setGuestValues(guestValues);
    const leadEntry = Object.entries(guestValues).find(([, guest]) => guest.isLeadGuest);
    setPrimaryGuestKey(leadEntry?.[0] ?? primaryGuestKey);
    track("Guest Data Submitted", { ...guestValues });
    const validity = validateStep("guests");
    goToNextStep(validity);
  };

  const handleNext = () => {
    const activeIndex = guests.findIndex((guest) => guest.key === activeKey);
    if (activeIndex < guests.length - 1) {
      setActiveKey(guests[activeIndex + 1].key);
      scrollIntoView();
    }
  };

  const handlePrev = () => {
    const activeIndex = guests.findIndex((guest) => guest.key === activeKey);
    setActiveKey(guests[Math.max(activeIndex - 1, 0)].key);
    scrollIntoView();
  };

  const handleError = (errors: ErrorInfo) => {
    const errorKey = (errors as any).errorFields.map((error: any) => error?.name?.[0])?.[0];
    if (guests.some((guest) => guest.key === errorKey)) {
      setActiveKey(errorKey);
    }
    scrollIntoView();
    invalidateStep("guests");
  };

  const formGuests = useMemo(
    () => generateInitialGuestValues(guests, guestValues, primaryGuestKey),
    [guests, guestValues, primaryGuestKey],
  );

  useEffect(() => {
    form.setFieldsValue(formGuests);
  }, [formGuests]);

  return (
    <Form form={form} layout="vertical" initialValues={formGuests} onFinish={handleFinish} onFinishFailed={handleError as any}>
      <div className="flex flex-col gap-3 rounded-sm">
        <div className="flex flex-col gap-3" ref={formContainerRef}>
          <GuestsFormList
            form={form}
            guests={guests}
            activeKey={activeKey}
            onActiveKeyChange={setActiveKey}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </div>
        <div>
          <StepButtons
            nextLabel={t("Hotels.booking.continueToPayment")}
            onNext={() => form.submit()}
            isNextDisabled={hasRateConditions && !policiesAccepted}
          />
        </div>
      </div>
    </Form>
  );
}
